import { useState } from "react";
import type { Question, StudyRecord, QuestionTracker } from "@shikaku/shared";
import {
  getDaysInMonth,
  getFirstDayOffset,
  isToday,
  addMonths,
  isSameMonth,
} from "@shikaku/shared";

interface HistoryViewProps {
  questions: Question[];
  record: StudyRecord;
  tracker: QuestionTracker;
  primaryColor: string;
}

const WEEKDAYS = ["月", "火", "水", "木", "金", "土", "日"];

export function HistoryView({
  questions,
  record,
  tracker,
  primaryColor,
}: HistoryViewProps) {
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const totalQ = questions.length;
  const correctQ = tracker.correctIDs.size;
  const ratio = totalQ > 0 ? correctQ / totalQ : 0;

  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOffset = getFirstDayOffset(year, month);
  const isCurrentMonth = isSameMonth(calendarMonth, new Date());

  const monthLabel = `${year}年${month + 1}月`;

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {/* Stats section */}
      <div style={{ background: "#fff", padding: "24px 20px", borderBottom: "1px solid #e8e8e8" }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#888", marginBottom: 4 }}>学習進捗</div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 4 }}>
            <span style={{ fontSize: 40, fontWeight: 700, color: primaryColor }}>{correctQ}</span>
            <span style={{ fontSize: 16, color: "#999" }}>/ {totalQ}問 正解済み</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="progress-bar-container" style={{ height: 12, maxWidth: 400, margin: "0 auto" }}>
          <div className="segment" style={{ width: `${Math.round(ratio * 100)}%`, background: primaryColor }} />
        </div>
        <div style={{ textAlign: "center", marginTop: 8, fontSize: 18, fontWeight: 700, color: primaryColor }}>
          {Math.round(ratio * 100)}%
        </div>

        {/* Streak */}
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 15, color: "#555" }}>
          🔥 {record.currentStreak}日連続学習中
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "#e8e8e8" }}>
        <StatCell label="総回答数" value={String(record.totalAnswered)} />
        <StatCell label="正答率" value={record.accuracyText} />
        <StatCell label="ブックマーク" value={String(tracker.bookmarkedIDs.size)} />
        <StatCell label="要復習" value={String(tracker.wrongIDs.size)} />
      </div>

      {/* Calendar */}
      <div style={{ background: "#fff", padding: "16px 20px", marginTop: 8, borderTop: "1px solid #e8e8e8" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#888", marginBottom: 12 }}>学習カレンダー</div>

        {/* Month navigation */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <button
            onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 8, color: "#888" }}
          >
            ◀
          </button>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{monthLabel}</span>
          <button
            onClick={() => { if (!isCurrentMonth) setCalendarMonth(addMonths(calendarMonth, 1)); }}
            style={{ background: "none", border: "none", cursor: isCurrentMonth ? "default" : "pointer", padding: 8, color: isCurrentMonth ? "#ddd" : "#888" }}
            disabled={isCurrentMonth}
          >
            ▶
          </button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
          {WEEKDAYS.map((d) => (
            <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 600, color: "#999" }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {Array.from({ length: firstDayOffset }, (_, i) => (
            <div key={`empty-${i}`} style={{ height: 36 }} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const date = new Date(year, month, day);
            const studied = record.studiedOn(date);
            const todayFlag = isToday(date);

            return (
              <div
                key={day}
                style={{
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  borderRadius: 6,
                  background: studied ? `${primaryColor}18` : "transparent",
                  border: todayFlag ? `2px solid ${primaryColor}` : "2px solid transparent",
                }}
              >
                <span style={{
                  fontSize: 13,
                  fontWeight: studied ? 700 : 400,
                  color: studied ? primaryColor : "#888",
                }}>
                  {day}
                </span>
                {studied && (
                  <div style={{
                    position: "absolute",
                    bottom: 2,
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: primaryColor,
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "#fff", padding: "16px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: "#333" }}>{value}</div>
      <div style={{ fontSize: 13, color: "#999", marginTop: 4 }}>{label}</div>
    </div>
  );
}
